import React, { useState, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useQuery } from "@tanstack/react-query";
import { Search, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { listResource, geocodeAddress } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScheduleBadge, scheduleInfo } from "@/components/common";

const CLARINDA = [40.7398, -95.0378];
const HOME = { lat: 40.7301218, lng: -95.0284125, label: "Whipp Sales & Service", address: "701 S 8th St, Clarinda, IA 51632" };

const homeIcon = L.divIcon({
  className: "",
  html: `<div style="transform:translate(-50%,-50%)">
    <div style="width:34px;height:34px;background:#16265F;border:3px solid #E0A81E;border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(0,0,0,.4)">
      <span style="color:#fff;font-family:'Cabinet Grotesk',sans-serif;font-weight:900;font-size:18px;line-height:1">W</span>
    </div></div>`,
  iconSize: [40, 40],
  iconAnchor: [0, 0],
});

const COLOR_HEX = { blue: "#2962FF", green: "#00C853", orange: "#C24E00", purple: "#7B1FA2", navy: "#0D1B5E", slate: "#475569", gray: "#9CA3AF" };

const pinIcon = (color) =>
  L.divIcon({
    className: "",
    html: `<div style="transform:translate(-50%,-100%)">
      <svg width="34" height="46" viewBox="0 0 24 32" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 0C5.4 0 0 5.4 0 12c0 8.4 12 20 12 20s12-11.6 12-20C24 5.4 18.6 0 12 0z"
          fill="${COLOR_HEX[color] || "#9CA3AF"}" stroke="#111827" stroke-width="1.5"/>
        <circle cx="12" cy="12" r="4.5" fill="#fff"/>
      </svg></div>`,
    iconSize: [34, 46],
    iconAnchor: [0, 0],
  });

function Recenter({ center }) {
  const map = useMap();
  React.useEffect(() => {
    if (center) map.flyTo(center, 12);
  }, [center, map]);
  return null;
}

export default function TruckingMap() {
  const [search, setSearch] = useState("");
  const [searching, setSearching] = useState(false);
  const [center, setCenter] = useState(null);
  const mapRef = useRef(null);

  const { data: stops = [] } = useQuery({
    queryKey: ["trucking"],
    queryFn: () => listResource("trucking"),
    refetchInterval: 5000,
  });

  const mapped = stops.filter((s) => !s.completed && typeof s.lat === "number" && typeof s.lng === "number");
  const unmapped = stops.length - mapped.length;

  const geocode = async () => {
    if (!search.trim()) return;
    setSearching(true);
    try {
      const { lat, lng } = await geocodeAddress(search);
      setCenter([lat, lng]);
    } catch {
      toast.error("Address not found");
    } finally {
      setSearching(false);
    }
  };

  return (
    <div data-testid="trucking-map-wrap" className="mb-8">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="flex items-stretch flex-1 min-w-[260px] max-w-md border-2 border-gray-900">
          <Input
            data-testid="map-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && geocode()}
            placeholder="Look up an address on the map…"
            className="rounded-none border-0 focus-visible:ring-0 text-base h-11"
          />
          <Button data-testid="map-search-btn" onClick={geocode} disabled={searching}
            className="rounded-none bg-gray-900 hover:bg-gray-700 h-11 px-4">
            {searching ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
          </Button>
        </div>
        <span className="text-sm text-gray-500">
          {mapped.length} stop{mapped.length === 1 ? "" : "s"} on map
          {unmapped > 0 && <span className="text-[#D50000]"> · {unmapped} missing a location</span>}
        </span>
      </div>

      <div className="border-2 border-gray-900" style={{ height: "460px" }}>
        <MapContainer center={CLARINDA} zoom={9} style={{ height: "100%", width: "100%" }} ref={mapRef}>
          <TileLayer attribution='&copy; OpenStreetMap contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <Recenter center={center} />
          <Marker position={[HOME.lat, HOME.lng]} icon={homeIcon} zIndexOffset={1000}>
            <Popup>
              <div className="min-w-[170px]" data-testid="home-marker-popup">
                <div className="font-bold text-base">{HOME.label}</div>
                <div className="text-gray-600 text-sm">{HOME.address}</div>
                <div className="text-xs uppercase tracking-widest font-bold text-[#16265F] mt-1">Home Base</div>
              </div>
            </Popup>
          </Marker>
          {mapped.map((s) => (
            <Marker key={s.id} position={[s.lat, s.lng]} icon={pinIcon(scheduleInfo(s.date)?.color || "gray")}>
              <Popup>
                <div className="min-w-[190px]" data-testid={`stop-popup-${s.id}`}>
                  <div className="font-bold text-base">{s.customer}</div>
                  <div className="text-gray-600 text-sm">{s.address}</div>
                  {s.deliver_unit && <div className="text-sm mt-1"><b>Deliver:</b> {s.deliver_unit}</div>}
                  {s.pickup_unit && <div className="text-sm"><b>Pick up:</b> {s.pickup_unit}</div>}
                  <div className="text-sm text-gray-500">{s.date} · {s.scheduled_by}</div>
                  <div className="mt-2"><ScheduleBadge date={s.date} /></div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
    </div>
  );
}
